import * as ko from "knockout";
import { type Computed, type components, type Observable, observable } from "knockout";
import * as Backend from "../../../backend/v1/sdk.gen";
import type { LocationPickerLocation } from "../../../backend/v1/types.gen";
import { translated } from "../../../lib/localize";
import type { HtmlDialog } from "../../../lib/popups";
import { htmlDialogStarter } from "../../dialogStarter";
import {
    type LocationIdString,
    type LocationItem,
    type LocationPickerParams,
    type PreselectLocationItem,
    type SelectType,
    typeIdMap,
} from "./locationPicker";

/**
 * Location Picker Popup Component
 *
 * Wrapping location-picker in popup dialog using dialog bindingHandler.
 * All params of location-picker will be passed through - read picker doc string.
 *
 * @param className - Additional class name(s) that will be assigned to
 * the container element that triggers the popup to open (f.e. for sizing)
 *
 * @param inputClassName - Additional class name(s) that will be assigned to
 * the input element that triggers the popup to open (f.e. for invalid data)
 *
 * @param inputId - String that should be used as id attribute on the input element
 * that triggers the popup to open. This can be usefull if used in forms with label elements.
 *
 * @param hidePopupTrigger - ko.observable used to hide the popup.
 *
 * @param afterApply - Function that will be executed when location picker
 * selection is applied/confirmed.
 */
interface LocationPickerPopupParams extends LocationPickerParams {
    className?: Record<string, () => any>;
    inputClassName?: Record<string, () => any>;
    inputId?: string;
    inputForm?: string;
    hidePopupTrigger?: Observable;
    afterApply?: () => void;
}

const showLocationPickerPopup = htmlDialogStarter(
    class {
        dialog: HtmlDialog;
        model: LocationPickerPopupViewModel;

        constructor(
            dialog: HtmlDialog,
            params: {
                title: string;
                eventTarget: HTMLElement;
                model: LocationPickerPopupViewModel;
                onClose: () => void;
            },
        ) {
            this.dialog = dialog;
            this.model = params.model;
        }
    },
    `
        <div class="location-picker-popup popup_frame" data-bind="with: model">
            <ko-location-picker params="selectedLocation: selectedLocation,
                                        initialized: initialized,
                                        cleanLocationsTrigger: cleanLocationsTrigger,
                                        unselectLocationTrigger: unselectLocationTrigger,
                                        pickerClassName: pickerClassName,
                                        selectType: selectType,
                                        withNoLocation: withNoLocation,
                                        parentLocation: parentLocation,
                                        disabled: disabledPicker,
                                        preselectLocation: preselectLocation,
                                        invalidationFunction: invalidationFunction,
                                        rememberSelectionTrigger: rememberSelectionTrigger,
                                        restoreSelectionTrigger: restoreSelectionTrigger,
                                        selectedLocationTitle: selectedLocationTitle,
                                        possibleLocations: possibleLocations,
                                        selectLocationTrigger: selectLocationTrigger"></ko-location-picker>
            <div class="popup_footer">
                <input type="reset"
                       data-bind="value: translated('Cancel'),
                                  click: hidePopup"/>
                <input type="submit"
                       class="confirm"
                       data-bind="value: translated('Apply'),
                                  click: confirmSelect,
                                  enable: confirmable"/>
            </div>
        </div>
    `,
    (params) => ({
        title: params.title,
        onClose: params.onClose,
        position: { anchor: params.eventTarget },
    }),
);

class LocationPickerPopupViewModel {
    dialog: ReturnType<typeof showLocationPickerPopup>;
    componentElement: HTMLElement;
    public initialized: Observable<false | "loading" | true>;
    public cleanLocationsTrigger: Observable;
    public unselectLocationTrigger: Observable;
    public restoreSelectionTrigger: Observable;
    public rememberSelectionTrigger: Observable;
    public className: Record<string, () => any>;
    public pickerClassName: string;
    public inputClassName: Record<string, () => any>;
    public inputId: string;
    public inputForm: string;
    public isConfirmed: boolean;
    public preselectLocation: PreselectLocationItem;
    public selectType: undefined | SelectType | SelectType[];
    public withNoLocation: undefined | false;
    public parentLocation: LocationIdString;
    public selectedLocation: Observable<LocationItem>;
    public selectedLocationTitle: Observable<string>;
    public disabledPicker: boolean;
    public disabledAttr: Observable<string | false>;
    public invalidationFunction: (arg0: LocationItem) => false | string;
    public possibleLocations: Observable<LocationPickerLocation[]>;
    public selectLocationTrigger: Observable<LocationItem>;
    public selectedLocationText: Computed<string>;
    public confirmable: Computed<boolean>;
    public afterApply: () => void;

    constructor(params: LocationPickerPopupParams, componentElement: HTMLElement) {
        this.componentElement = componentElement;
        this.initialized = ko.observable(false);
        this.cleanLocationsTrigger = ko.observable();
        this.unselectLocationTrigger = ko.observable();
        this.rememberSelectionTrigger = ko.observable();
        this.restoreSelectionTrigger = ko.observable();
        this.className = params.className;
        this.pickerClassName = params.pickerClassName;
        this.inputClassName = params.inputClassName;
        this.inputId = params.inputId;
        this.inputForm = params.inputForm;
        this.isConfirmed = false;
        this.preselectLocation = params.preselectLocation;
        this.selectType = params.selectType;
        this.withNoLocation = params.withNoLocation;
        this.parentLocation = params.parentLocation;
        this.selectedLocation = ko.observable();
        this.selectedLocationTitle = ko.observable();
        this.disabledPicker = params.disabled;
        this.disabledAttr = ko.observable(this.disabledPicker ? "disabled" : false);
        this.invalidationFunction = params.invalidationFunction;
        this.possibleLocations = params.possibleLocations;
        this.selectLocationTrigger = params.selectLocationTrigger;
        this.selectedLocationText = ko.pureComputed(() => {
            if (this.initialized() === "loading") {
                return translated("Loading");
            }
            if (this.selectedLocation()) {
                return this.selectedLocation().full_name;
            }
            if (this.disabledPicker) {
                return translated("No location set");
            }
            return "";
        });

        this.confirmable = ko.pureComputed(() => {
            return this.selectedLocation() instanceof Object;
        });

        this.selectedLocation.subscribe((value) => {
            if (ko.isObservable(params.selectedLocation) && params.selectedLocation() !== value) {
                params.selectedLocation(value);
            }
        });

        if (ko.isObservable(params.selectedLocation)) {
            this.selectedLocation(params.selectedLocation());
            params.selectedLocation.subscribe((value) => {
                if (this.selectedLocation() !== value) {
                    this.selectedLocation(value);
                }
            });
        }

        this.selectedLocationTitle.subscribe((value) => {
            if (ko.isObservable(params.selectedLocationTitle)) {
                params.selectedLocationTitle(value);
            }
        });

        this.selectedLocationTitle.subscribe((value) => {
            if (ko.isObservable(params.selectedLocationTitle)) {
                params.selectedLocationTitle(value);
            }
        });

        if (ko.isObservable(params.initialized)) {
            params.initialized(this.initialized());
            this.initialized.subscribe((value) => {
                params.initialized(value);
            });
        }

        if (ko.isObservable(params.unselectLocationTrigger)) {
            // trigger with params.unselectLocationTrigger.valueHasMutated()
            params.unselectLocationTrigger.extend({ notify: "always" });
            params.unselectLocationTrigger.subscribe(() => {
                this.unselectLocationTrigger.valueHasMutated();
            });
        }

        if (ko.isObservable(params.cleanLocationsTrigger)) {
            // trigger with params.cleanLocationsTrigger.valueHasMutated()
            params.cleanLocationsTrigger.extend({ notify: "always" });
            params.cleanLocationsTrigger.subscribe(() => {
                this.cleanLocationsTrigger.valueHasMutated();
            });
        }

        if (ko.isObservable(params.hidePopupTrigger)) {
            params.hidePopupTrigger.extend({ notify: "always" });
            params.hidePopupTrigger.subscribe(() => {
                this.hidePopup();
            });
        }

        if (Number.isInteger(this.preselectLocation?.id)) {
            Backend.getPickerLocation({
                path: { location_type: this.preselectLocation.type, location_id: this.preselectLocation.id },
            })
                .then(({ data: location }) => {
                    const locationIdName = typeIdMap[location.type];
                    this.selectedLocation({
                        active: observable(true),
                        locationId: { [locationIdName]: location[locationIdName] },
                        ...location,
                    });
                })
                .finally(() => this.initialized(true));
        }

        this.afterApply = () => {
            if (typeof params.afterApply === "function") {
                params.afterApply();
            }
        };
    }

    public showPopup = () => {
        if (this.disabledPicker) {
            return;
        }
        this.isConfirmed = false;
        this.dialog = showLocationPickerPopup({
            eventTarget: this.componentElement,
            model: this,
            title:
                this.selectType === "building"
                    ? translated("Select building")
                    : this.selectType === "area"
                      ? translated("Select area")
                      : this.selectType === "room"
                        ? translated("Select room")
                        : this.selectType === "rack"
                          ? translated("Select rack")
                          : this.selectType === "transfer"
                            ? translated("Select transfer destination")
                            : translated("Select location"),
            onClose: () => this.closeCallback(this.componentElement),
        });
        this.cleanLocationsTrigger.valueHasMutated();
        this.rememberSelectionTrigger.valueHasMutated();
    };

    public hidePopup = () => {
        this.dialog?.dialog?.close();
    };

    public confirmSelect = () => {
        this.isConfirmed = true;
        this.afterApply();
        this.hidePopup();
    };

    private closeCallback = (componentElement: HTMLElement) => {
        if (!this.isConfirmed) {
            this.restoreSelectionTrigger.valueHasMutated();
        }
        //lose focus of cage location (textarea), so location-picker-popup shows up on next click
        const blurEvent = document.createEvent("HTMLEvents");
        blurEvent.initEvent("blur", true, false);
        componentElement.dispatchEvent(blurEvent);
    };
}

export class LocationPickerPopupComponent {
    constructor() {
        // biome-ignore lint/correctness/noConstructorReturn:
        return {
            viewModel: {
                createViewModel: (params: LocationPickerPopupParams, componentInfo: components.ComponentInfo) => {
                    let viewModelElement: ChildNode;
                    const componentElement = componentInfo.element as HTMLElement;
                    if (componentElement.nodeName === "#comment") {
                        viewModelElement = componentElement.nextElementSibling;
                    } else {
                        viewModelElement = componentElement.firstChild;
                    }
                    return new LocationPickerPopupViewModel(params, viewModelElement as HTMLElement);
                },
            },
            template: `
                <div class="tw:flex tw:gap-1"
                    data-bind="css: className">
                    <input class="tw:grow"
                           readonly="readonly"
                           type="text"
                           data-bind="value: selectedLocationText,
                                      css: inputClassName,
                                      attr: {title: selectedLocationTitle,
                                             id: inputId,
                                             form: inputForm,
                                             disabled: disabledAttr},
                                      "></input>
                    <button type="button"
                            data-bind="click: showPopup,
                                       hidden: disabledPicker,
                                       text: translated('Select')"></button>
                </div>
            `,
        };
    }
}
